import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, select, put, takeEvery } from 'redux-saga/effects';
import { RootState } from 'types';
import {
  IControlsState,
  interact,
  IPlayerState,
  IVideoState,
  IViewer,
  updateControlsState,
  updatePlayerState,
  updateVideoState,
  updateViewerState,
  ViewerState,
} from 'core/slices/player';
import { Mode } from 'core/slices/session';
import { ActionBehaviour, ActionType, IActionBasic } from 'types/domain/action';
import { selectAction, selectActionVoiceSceneId } from 'core/selectors/product/action/action';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { decrementIndex, incrementIndex } from 'core/slices/register';
import { selectPortrait } from 'core/selectors/product/portrait';
import { ISceneDAO, SceneType } from 'types/domain/scene';
import { selectActionEditor } from 'core/slices/actionEditor/selectActionEditor';
import { IActionEditorState } from 'core/slices/actionEditor';
import { selectScene } from 'core/selectors/product/scene/scene';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { selectVoiceSceneConfig } from 'core/selectors/product/scene/sceneConfig';

function* updateViewerStateSaga(action: PayloadAction<IViewer>) {
  if (action.payload.state === ViewerState.RESTING) yield canPlayVideo();
}

function* updateVideoStateSaga(action: PayloadAction<Partial<IVideoState>>) {
  if (action.payload.interacted || action.payload.ready) yield canPlayVideo();
}

function* updateControlsStateSaga(action: PayloadAction<Partial<IControlsState>>) {
  if (action.payload.animating === false) yield canPlayVideo();
}

function* updateIndexSaga() {
  yield canPlayVideo();
}

export function* canPlayVideo(userPaused?) {
  const state: IPlayerState = yield select((state: RootState) => state.core.player.state);
  const viewer: IViewer = yield select((state: RootState) => state.core.player.state.viewer);
  // const mode: Mode = yield select(selectSessionMode);
  const feed: IActionFeed = yield select(selectActionFeed);

  const actionDAO: IActionBasic = yield select(state =>
    //@ts-ignore
    feed.feed[feed.index]?.actionId ? selectAction(state, feed.feed[feed.index].actionId) : null,
  );
  const actionEditor: IActionEditorState = yield select(selectActionEditor);

  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  const voiceSceneConfig = yield selectVoiceSceneConfig;

  const hasVideo = (scene && (scene?.type === SceneType.VIDEO || !!voiceSceneConfig)) || actionDAO?.youTubeId;

  if (!hasVideo || actionEditor?.enabled || (scene && actionDAO?.sceneId !== scene.id)) return;

  const shouldPlay = actionDAO?.behaviour !== ActionBehaviour.PAUSE || actionDAO?.voiceSceneId || actionDAO?.youTubeId;

  const canPlay =
    shouldPlay && viewer.state === ViewerState.RESTING && state.video.interacted && !state.controls.animating;

  //   shouldPlay,
  //   viewer: viewer.state,
  //   videoInteracted: state.video.interacted,
  //   controlsAnimating: state.controls.animating,
  //   canPlay,
  //   actionDAO,
  //   scene,
  //   hasVideo,
  // });

  if (canPlay)
    yield put(
      yield call(updatePlayerState, {
        paused: false,
        userPaused,
        faded: actionDAO.type !== ActionType.INTERACTIVE,
        culprit: 'canPlayVideo',
      }),
    );
}

function* interactSaga(action: PayloadAction<number>) {
  const state: IPlayerState = yield select((state: RootState) => state.core.player.state);
  const portrait = yield select(selectPortrait);
  const actionBasic: IActionBasic = yield select(state => selectAction(state, undefined));
  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  const actionEditor: IActionEditorState = yield select(selectActionEditor);
  const mode: Mode = yield select(selectSessionMode);
  const actionVoiceSceneId = yield select(state => selectActionVoiceSceneId(state, undefined));
  const hasVideo = (scene && scene.type === SceneType.VIDEO) || actionVoiceSceneId || !!actionBasic?.youTubeId;

  if (actionBasic?.youTubeId)
    return yield put(yield call(updatePlayerState, { youTubePaused: !state.youTubePaused, userPaused: true }));

  if (
    !hasVideo ||
    state.controls.animating ||
    state.viewer.state === ViewerState.TRANSITIONING ||
    actionEditor?.enabled ||
    mode === Mode.ADMIN
  )
    return;

  if (portrait && (action.payload < 0.3 || action.payload > 0.7)) return;

  if (state.paused) yield canPlayVideo(true);
  else yield put(yield call(updatePlayerState, { paused: true, userPaused: true, culprit: 'interact' }));
}

function* watchUpdateVideoState() {
  yield takeEvery(updateVideoState.type, updateVideoStateSaga);
}

function* watchUpdateViewerState() {
  yield takeEvery(updateViewerState, updateViewerStateSaga);
}

function* watchUpdateControlsState() {
  yield takeEvery(updateControlsState, updateControlsStateSaga);
}

function* watchIndex() {
  yield takeEvery([incrementIndex, decrementIndex], updateIndexSaga);
}

function* watchInteract() {
  yield takeEvery(interact, interactSaga);
}

export function* playSaga() {
  yield all([
    fork(watchUpdateVideoState),
    fork(watchIndex),
    fork(watchUpdateViewerState),
    fork(watchInteract),
    fork(watchUpdateControlsState),
  ]);
}
